import type { MatterPhaseAction } from "@/lib/types";

interface ActionProgress {
  label: string;
  completed?: number;
  total?: number;
}

const isCount = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

/** Only display the evaluator's known aggregate contracts, never raw evidence. */
export function getActionProgress(
  action: MatterPhaseAction,
): ActionProgress | null {
  const parameters = action.reasonParameters;
  const key = action.source.actionDefinitionKey;
  if (action.reasonCode === "APPOINTMENT_PROGRESS") {
    const count = parameters.upcomingAppointmentCount;
    return isCount(count)
      ? {
          label:
            count === 0
              ? "No upcoming appointments"
              : `${count} upcoming appointment${count === 1 ? "" : "s"}`,
        }
      : null;
  }

  if (
    key === "insurance.first_party_coverage_resolved" &&
    action.reasonCode === "COVERAGE_DISPOSITION_PROGRESS"
  ) {
    const {
      itemCount,
      clearedItemCount,
      deniedItemCount,
      unresolvedItemCount,
    } = parameters;
    if (
      !isCount(itemCount) ||
      !isCount(clearedItemCount) ||
      !isCount(deniedItemCount) ||
      !isCount(unresolvedItemCount) ||
      clearedItemCount + deniedItemCount + unresolvedItemCount !== itemCount
    )
      return null;
    return itemCount === 0
      ? { label: "No qualifying policies linked" }
      : {
          label: `${clearedItemCount} cleared · ${deniedItemCount} denied · ${unresolvedItemCount} unresolved`,
          completed: clearedItemCount + deniedItemCount,
          total: itemCount,
        };
  }

  const providers = action.reasonCode === "PROVIDER_PROGRESS";
  if (!providers && action.reasonCode !== "ITEM_PROGRESS") return null;
  const total = providers ? parameters.providerCount : parameters.itemCount;
  const completed = providers
    ? parameters.completedProviderCount
    : parameters.completedItemCount;
  if (!isCount(total) || !isCount(completed) || completed > total) return null;
  const factLabels: Record<string, [string, string]> = {
    "demand.draft_exists": [
      "No settlement demand recorded",
      "Settlement demand recorded",
    ],
    "demand.sent_to_carrier": [
      "No carrier demand sent date recorded",
      "Carrier demand sent date recorded",
    ],
    "matter.closed": [
      "Matter is not in a closed status",
      "Matter is in a closed status",
    ],
    "insurance.coverage_tracked": [
      "No insurance coverage linked",
      "Insurance coverage linked",
    ],
  };
  const fact = key ? factLabels[key] : undefined;
  if (fact) return { label: fact[completed > 0 ? 1 : 0] };
  if (key === "medical.providers_added")
    return {
      label:
        total === 0
          ? "No providers added yet"
          : `${total} ${total === 1 ? "provider" : "providers"} added`,
    };
  const policyProgress: Record<string, string> = {
    "insurance.third_party_coverage_resolved": "resolved",
    "insurance.first_party_um_resolved": "resolved",
    "insurance.first_party_uim_resolved": "resolved",
    "insurance.liability_decision_recorded": "with a liability decision",
    "insurance.first_party_liability_and_limits_recorded":
      "with liability and policy limits recorded",
    "insurance.liability_accepted": "with accepted liability",
    "insurance.coverage_confirmed": "with confirmed coverage",
  };
  const policyLabel = key ? policyProgress[key] : undefined;
  if (policyLabel)
    return total === 0
      ? { label: "No qualifying policies linked" }
      : {
          label: `${completed} of ${total} ${total === 1 ? "policy" : "policies"} ${policyLabel}`,
          completed,
          total,
        };
  if (key === "incident.reports_received")
    return total === 0
      ? { label: "No incident reports recorded" }
      : {
          label: `${completed} of ${total} report requirements satisfied`,
          completed,
          total,
        };
  if (total === 0)
    return {
      label: providers ? "No providers added yet" : "No items recorded yet",
    };
  return {
    label: `${completed} of ${total} ${providers ? "provider" : "item"}${total === 1 ? "" : "s"} complete`,
    completed,
    total,
  };
}
